// UploadsExtractedProvider - wrap a list surface in this and every descendant
// useUploadExtracted(uploadId) transparently serves from ONE batched query (its
// standalone /uploads/extracted request is disabled). Surfaces with no provider
// (the lazy, expand-gated Drive/Gmail synced-files trees) keep their standalone
// per-item behaviour unchanged. See useUploadsExtracted.ts for the why.
import { useMemo, type ReactNode } from "react";
import {
  UploadsExtractedContext,
  useUploadsExtracted,
  type UploadsExtractedContextValue,
} from "./useUploadsExtracted";

export const UploadsExtractedProvider = ({
  uploadIds,
  children,
}: {
  uploadIds: string[];
  children: ReactNode;
}): JSX.Element => {
  const query = useUploadsExtracted(uploadIds);
  // Stabilise on the id-set CONTENT so the context value only changes when the
  // set or the query result actually changes - not on every route re-render
  // (uploadIds is a fresh array from .map each time).
  const idsKey = Array.from(new Set(uploadIds)).sort().join(",");
  const value = useMemo<UploadsExtractedContextValue>(() => {
    const owned = new Set(idsKey ? idsKey.split(",") : []);
    const views = query.data ?? {};
    return {
      owns: (uploadId: string) => owned.has(uploadId),
      resultFor: (uploadId: string) => ({
        data: views[uploadId],
        // "Loading" only until the first batch load lands; once loaded, an
        // absent (owned) id means deleted, not still-loading.
        isLoading: query.isLoading && !(uploadId in views),
        isError: query.isError,
        error: query.error,
      }),
    };
  }, [idsKey, query.data, query.isLoading, query.isError, query.error]);
  return (
    <UploadsExtractedContext.Provider value={value}>
      {children}
    </UploadsExtractedContext.Provider>
  );
};
